import React, { useEffect, useMemo, useState } from "react";
import { useAddViewerToGroupMutation } from "../../redux/admin/groupApi";
import { useGetAssignableUsersQuery } from "../../redux/admin/adminApi";
import { useGetUserProfileQuery } from "../../redux/admin/userApi";
import { toast } from "react-hot-toast";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  CircularProgress,
  Skeleton,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { X, Search, Mail, Phone, Eye, UserPlus, Check } from "lucide-react";

const getRolesToFetch = (loggedInUserRole) => {
  const role = loggedInUserRole?.toLowerCase();
  switch (role) {
    case "admin":
    case "manager":
      return ["sub_admin", "group_admin", "client", "user", "viewer"];
    case "hr":
      return ["group_admin", "client", "user", "viewer"];
    case "office_emp":
      return ["sub_admin", "group_admin", "client", "user", "viewer"];
    case "sub_admin":
      return ["group_admin", "client", "user", "viewer"];
    case "group_admin":
      return ["client", "user", "viewer"];
    case "user":
      return ["client", "viewer"];
    case "client":
      return ["viewer"];
    default:
      return ["viewer"];
  }
};

const prettyRole = (role) => {
  const map = {
    admin: "Admin",
    Manager: "manager",
    HR: "hr",
    hr: "hr",
    office_emp: "Office Emp",
    field_emp: "Field Emp",
    sub_admin: "Admin",
    group_admin: "Dealer",
    client: "Client",
    user: "User",
    viewer: "Viewer",
  };
  return map[role] || role;
};

const roleChipColor = (role) => {
  const r = (role || "").toLowerCase();
  if (r === "admin") return "error";
  if (r === "sub_admin") return "info";
  if (r === "group_admin") return "warning";
  if (r === "client") return "secondary";
  if (r === "viewer") return "default";
  return "primary";
};

const AddViewerModal = ({ groupId, isOpen, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedUserIds([]);
    }
  }, [isOpen]);

  const { data: userProfile, isLoading: isProfileLoading } =
    useGetUserProfileQuery(undefined, { skip: !isOpen });

  const loggedInUserRole = userProfile?.role || "user";
  const rolesToFetch = useMemo(
    () => getRolesToFetch(loggedInUserRole),
    [loggedInUserRole]
  );

  const {
    data: assignableUsers,
    isLoading: isUsersLoading,
    error,
  } = useGetAssignableUsersQuery(rolesToFetch, {
    skip: !isOpen || isProfileLoading,
  });

  const [addViewer, { isLoading: isAdding }] = useAddViewerToGroupMutation();

  const users = useMemo(
    () => (Array.isArray(assignableUsers) ? assignableUsers : []),
    [assignableUsers]
  );

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.username?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.mobileNo?.includes(q)
    );
  }, [users, searchQuery]);

  const isLoading = isProfileLoading || isUsersLoading;

  const handleToggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    setSelectedUserIds(filteredUsers.map((u) => u._id));
  };

  const handleClearSelection = () => {
    setSelectedUserIds([]);
  };

  const handleSubmit = async () => {
    if (!groupId) return toast.error("Group not found");
    if (selectedUserIds.length === 0) return toast.error("Please select at least one user");

    try {
      let successCount = 0;
      let failCount = 0;

      // ✅ same as your logic (per-user call)
      for (const userId of selectedUserIds) {
        try {
          await addViewer({ groupId, userId }).unwrap();
          successCount++;
        } catch (err) {
          console.error("Failed to add user:", userId, err);
          failCount++;
        }
      }

      if (successCount) toast.success(`${successCount} viewer(s) added successfully!`);
      if (failCount) toast.error(`${failCount} user(s) failed to add`);

      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e?.data?.msg || "Failed to add viewers");
    }
  };

  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, overflow: "hidden" } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          p: 2,
          background: alpha(theme.palette.warning.main, 0.12),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={0.3}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2.2,
                  display: "grid",
                  placeItems: "center",
                  background: theme.palette.warning.main,
                  color: "white",
                }}
              >
                <Eye size={18} />
              </Box>
              <Typography variant="h6" fontWeight={900}>
                Add Viewers
              </Typography>
              {isLoading && (
                <Chip size="small" label="Loading…" variant="outlined" sx={{ borderRadius: 2, fontWeight: 800 }} />
              )}
            </Stack>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Assign users to view devices in this group.
            </Typography>
          </Stack>

          <Tooltip title="Close">
            <IconButton onClick={onClose} sx={{ borderRadius: 2 }}>
              <X size={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      {/* Toolbar */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          background: theme.palette.background.paper,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "stretch", sm: "center" }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or phone…"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, maxWidth: { sm: 520 } }}
          />

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleSelectAll}
              disabled={filteredUsers.length === 0 || isLoading}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              Select All
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearSelection}
              disabled={selectedUserIds.length === 0}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>

        <Typography variant="caption" sx={{ color: "text.secondary", mt: 1, display: "block" }}>
          Your role: <b>{prettyRole(loggedInUserRole)}</b> • Can assign:{" "}
          {rolesToFetch.map(prettyRole).join(", ")}
        </Typography>
      </Box>

      <DialogContent sx={{ p: 2, background: alpha(theme.palette.background.default, 0.35) }}>
        {error ? (
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.9)}` }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error?.data?.msg || "Error loading users. Please try again."}
            </Alert>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              overflow: "hidden",
            }}
          >
            {isLoading ? (
              <Box sx={{ p: 2 }}>
                <Stack spacing={1.2}>
                  {Array.from({ length: 7 }).map((_, i) => (
                    <Box key={i} sx={{ p: 1.2 }}>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Skeleton variant="rounded" width={42} height={42} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton width="45%" />
                          <Skeleton width="70%" />
                        </Box>
                        <Skeleton width={36} height={36} variant="rounded" />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : filteredUsers.length > 0 ? (
              <Box sx={{ maxHeight: fullScreen ? "calc(100vh - 300px)" : 420, overflow: "auto" }}>
                {filteredUsers.map((u, idx) => {
                  const selected = selectedUserIds.includes(u._id);
                  return (
                    <Box key={u._id}>
                      <Box
                        onClick={() => handleToggleUser(u._id)}
                        sx={{
                          px: 1.5,
                          py: 1.2,
                          cursor: "pointer",
                          background: selected
                            ? alpha(theme.palette.warning.main, 0.16)
                            : theme.palette.background.paper,
                          "&:hover": { background: alpha(theme.palette.warning.main, 0.1) },
                          transition: "0.12s ease",
                        }}
                      >
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: 2.3,
                              display: "grid",
                              placeItems: "center",
                              background: alpha(theme.palette.warning.main, 0.16),
                              color: theme.palette.warning.dark,
                              fontWeight: 900,
                              flexShrink: 0,
                            }}
                          >
                            {(u.username?.[0] || "?").toUpperCase()}
                          </Box>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography fontWeight={900} noWrap>
                                {u.username || "Unknown"}
                              </Typography>
                              <Chip
                                size="small"
                                label={prettyRole(u.role)}
                                color={roleChipColor(u.role)}
                                variant="outlined"
                                sx={{ borderRadius: 2, fontWeight: 800 }}
                              />
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                                <Mail size={12} style={{ marginRight: 6, verticalAlign: "middle" }} />
                                {u.email || "No email"}
                              </Typography>

                              {u.mobileNo ? (
                                <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                                  <Phone size={12} style={{ marginRight: 6, verticalAlign: "middle" }} />
                                  {u.mobileNo}
                                </Typography>
                              ) : null}
                            </Stack>
                          </Box>

                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                              background: selected ? theme.palette.warning.main : "transparent",
                              color: selected ? "white" : "transparent",
                            }}
                          >
                            <Check size={18} />
                          </Box>
                        </Stack>
                      </Box>

                      {idx !== filteredUsers.length - 1 && <Divider />}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography fontWeight={900}>No users found</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {searchQuery ? "Try a different search term." : "No assignable users available."}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          background: theme.palette.background.paper,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%" }}
          alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {selectedUserIds.length > 0
              ? `${selectedUserIds.length} user${selectedUserIds.length > 1 ? "s" : ""} selected`
              : `${filteredUsers.length} user${filteredUsers.length !== 1 ? "s" : ""} available`}
          </Typography>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onClose} variant="outlined" disabled={isAdding} sx={{ borderRadius: 2, fontWeight: 800 }}>
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isAdding || selectedUserIds.length === 0}
              startIcon={
                isAdding ? (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                ) : (
                  <UserPlus size={18} />
                )
              }
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                backgroundColor: theme.palette.warning.main,
                "&:hover": { backgroundColor: theme.palette.warning.dark },
              }}
            >
              {isAdding ? "Adding…" : "Add Viewers"}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AddViewerModal;
